import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Feather, CheckCircle, BookOpen, Loader2 } from 'lucide-react';

import { API } from '../config';

export default function SubscriptionSuccess() {
  const { token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/status/${sessionId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        toast.success('Subscription activated! Welcome to Unspoken Hearts.');
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired.');
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="bg-parchment border-b border-[#E5E0D0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Feather className="w-6 h-6 text-wine" />
            <span className="font-playfair text-xl text-charcoal">Unspoken Hearts</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        {status === 'checking' && (
          <div className="animate-fade-in">
            <Loader2 className="w-16 h-16 text-wine mx-auto mb-6 animate-spin" />
            <h1 className="font-playfair text-3xl text-charcoal mb-4">Verifying Payment</h1>
            <p className="font-lora text-charcoal-muted">Please wait while we confirm your subscription...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-8 bg-rose-muted/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-wine" />
            </div>
            <h1 className="font-playfair text-4xl text-charcoal mb-4">Welcome to Unspoken Hearts</h1>
            <p className="font-lora text-lg text-charcoal-muted mb-8 max-w-md mx-auto">
              Your subscription is now active. The first letter of Clara and James's story awaits you.
            </p>
            
            <div className="space-y-4">
              <Button 
                onClick={() => navigate('/letter/1')}
                className="btn-primary"
                data-testid="read-first-letter-btn"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                Read Your First Letter
              </Button>
              <div>
                <Button 
                  onClick={() => navigate('/archive')}
                  variant="ghost"
                  className="font-lora text-charcoal-muted hover:text-charcoal"
                  data-testid="browse-archive-btn"
                >
                  Or browse the archive
                </Button>
              </div>
            </div>

            <div className="mt-12 p-6 bg-parchment-paper border border-[#E5E0D0] rounded-sm">
              <p className="font-dancing text-xl text-sepia mb-2">A note from us</p>
              <p className="font-lora text-charcoal-muted italic">
                Thank you for joining our community of readers. We hope these letters bring you 
                moments of quiet reflection and the timeless magic of a love story told through ink.
              </p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-8 bg-rose-muted/20 rounded-full flex items-center justify-center">
              <Feather className="w-12 h-12 text-charcoal-muted" />
            </div>
            <h1 className="font-playfair text-3xl text-charcoal mb-4">Something Went Wrong</h1>
            <p className="font-lora text-charcoal-muted mb-8">
              We couldn't verify your payment. If you were charged, please contact support.
            </p>
            <Button 
              onClick={() => navigate('/subscription')}
              className="btn-secondary"
              data-testid="try-again-btn"
            >
              Try Again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
